import { Events } from '@type-r/models';
import { useEvent, useIO, useLinked, useModel, useModelCopy } from '@type-r/react';
import { InvoiceModel } from 'api/invoice';
import { Loading, NavTree, NavTreeItem, PageLayout } from 'lqx-react';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { getModelsJsonDiff } from '../../../../../common';
import { Shipment } from '../../../../../common/shipment';
import { Shipping } from '../../../../../common/shipping';
import { AdditionalInfo } from './additionalInfo';
import { DialogType, ShowDialog } from './dialogs';
import { InvoiceHeader } from './header';
import { InvoiceManagement } from './InvoiceManagement';
import { UpdateSection } from './updateSection';

const compareJsonProperties = {
    includeAttr: [
        'parts'
    ]
};

function applyDisputedChanges(disputedInvoice: InvoiceModel) {
    disputedInvoice.transaction(() => {
        disputedInvoice.disputes
        .filter(disputeEntry => disputeEntry.isActiveDispute
            || (disputedInvoice.invoiceStatus === 'ApprovalNeeded' && disputeEntry.isApproved))
        .forEach(disputeEntry => {
            if (!disputeEntry.isActiveDispute || disputeEntry.isUserOnTheActiveSide) {
                disputeEntry.history.forEach(h => {
                    disputedInvoice.set(h.change, { parse: true, remove: false, merge: true });
                });
                return;
            }

            let possibleChanges = [];
            disputeEntry.history.forEach(historyEntry => {
                possibleChanges.push(historyEntry.change);
                if (historyEntry.status === 'Disputed') {
                    possibleChanges.forEach(h => {
                        disputedInvoice.set(h, { parse: true, remove: false, merge: true });
                    });
                    possibleChanges = [];
                }
            });

            if (disputedInvoice.isSuperSupplier
                && (disputeEntry.history.last().activeSideRole === 'Customer') === disputedInvoice.isCustomer
            ) {
                possibleChanges.forEach(h => {
                    disputedInvoice.set(h, { parse: true, remove: false, merge: true });
                });
            }
        });
    });
}

export const InvoiceDetails = ({ invoiceId }) => {
    const prevInvoice = useModel( InvoiceModel ),
        disputedInvoice = useModelCopy( prevInvoice ),
        invoice = useModelCopy( disputedInvoice );

    // Opened dialog state
    const $dialog = useLinked<DialogType>( null );
    const loadInvoice = async () => {
        prevInvoice.id = invoiceId;
        await Promise.all([
            prevInvoice.fetch(),
        ]);
    }
    const isReady = useIO(async () => await loadInvoice() , [invoiceId]);

    useEvent(Events, "Invoice:close", async () => await loadInvoice() );
    useEvent(Events, "Invoice:refresh", async () => await loadInvoice() );

    useEffect(() => {
        if (disputedInvoice.disputes) {
            applyDisputedChanges(disputedInvoice);
        }
    }, [(disputedInvoice.disputes as any)._changeToken]);

    const isInvoiceChanged = useMemo(() => {
        const diff = getModelsJsonDiff(disputedInvoice, invoice, compareJsonProperties);
        const hasChanges = Object.keys(diff).length > 0;
        return hasChanges;
    }, [(invoice as any)._changeToken]);

    // We should refresh events log and uploads when the uploads dialog closes.
    // useIO( async () => {
    //     await Promise.all([
    //         invoice.logs.fetch(),
    //         invoice.uploads.fetch(),
    //     ])
    // }, [orderId, $upload.value ]);

    return isReady ? (
        <PageLayout
            menu={<Menu invoice={ invoice }/>}
            header={
                <InvoiceHeader
                    $dialog={ $dialog }
                    invoice={invoice}
                    prevInvoice={prevInvoice}
                    isInvoiceChanged={isInvoiceChanged}
                />}
            subheader={ -90 }
        >
            <UpdateSection
                prevInvoice={ prevInvoice }
                disputedInvoice={ disputedInvoice }
            />
            <InvoiceManagement invoice={ invoice } original={prevInvoice}/>
            <Shipping
                bill_to={ invoice.bill_to}
                ship_to={ invoice.ship_to }
                end_customer={ invoice.end_customer }
                shipping={ invoice.shipping }
            />

            { invoice.shipment.length ?
                <Shipment shipment={ invoice.shipment }/>
            : undefined }

            {/*
            { invoice.invoicesInfo.length ?
                <Invoices info={ invoice.invoicesInfo }/>
            : undefined }
            */}

            <AdditionalInfo
                invoice={ invoice }
                prevInvoice={ prevInvoice }
                $dialog={ $dialog }
            />
            
            <br/>

            <ShowDialog
                $dialog={ $dialog }
                invoice={ invoice }
                disputedInvoice={ disputedInvoice }
                prevInvoice={ prevInvoice }
            />
        </PageLayout>
    ) :
        <Loading />
}

const Menu = ({ invoice } : {
    invoice : InvoiceModel
}) =>
    <NavTree
        defaultExpanded={["invoice-management", "shipping", "shipment", "invoice", "additional-info"]}
    >
        <NavTreeItem label="Invoice Management"  target="invoice-management">
            <NavTreeItem label="Invoice Details"  target="invoice-details" />
            <NavTreeItem label="Components"  target="components"/>
        </NavTreeItem>

        <NavTreeItem label="Shipping Details"  target="shipping-details"></NavTreeItem>

        { invoice.shipment.length ?
            <NavTreeItem label="Shipping Notice" target="shipment">
            { invoice.shipment.map( x =>
                <NavTreeItem key={ x.cid } label={ x.packing_slip_number }  target={x.cid}/>
            )}
            </NavTreeItem>
        : undefined }

        <NavTreeItem label="Additional Information"  target="additional-info">
            <NavTreeItem label="Payment Details"  target="payment-details" />
            <NavTreeItem label="Financing"  target="financing"/>
            <NavTreeItem label="Documents"  target="documents"/>
            <NavTreeItem label="All Activity"  target="all-activity"/>
        </NavTreeItem>
    </NavTree>
